import { useState, useEffect } from "react";
import {
  FaArrowLeft,
  FaPhone,
  FaEnvelope,
  FaMoneyBillWave,
  FaBriefcase,
  FaCheckCircle,
  FaClock,
  FaUsers,
  FaTicket,
  FaWifi,
  FaCircleExclamation,
  FaArrowTrendUp,
  FaArrowTrendDown,
} from "react-icons/fa6";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../../../components/layouts/DashboardLayout";
import { Header } from "../../../../../components/Header";
import { BaseUrl } from "../../../../../utilities/BaseUrl";
import { FaCalendarAlt, FaMapMarkerAlt, FaUserCircle } from "react-icons/fa";

/* ============================================
   Dummy Data Mitra
   ============================================ */
const DUMMY_MITRA = {
  id: "mitra-001",
  name: "PT. Danela Net Serang",
  email: "mitra@danelanet.com",
  phone_number: "0812-3456-7890",
  role: { name: "Mitra" },
  status: { name: "Aktif" },
  image: null,
  address: "Jl. Raya Serang No. 12, Kota Serang, Banten",
  join_date: "2022-01-15",
  // Penghasilan
  earnings: {
    bulan_ini: 8500000,
    bulan_lalu: 7200000,
    total_tahun_ini: 68500000,
    trend: "up", // "up" | "down"
  },
  // Statistik
  stats: {
    total_user_pppoe: 156,
    user_aktif: 142,
    user_nonaktif: 14,
    voucher_aktif: 23,
    voucher_terpakai: 187,
    voucher_total: 210,
  },
  // Hutang Piutang
  financials: {
    total_piutang: 2500000,
    total_hutang: 750000,
    saldo: 1750000, // piutang - hutang
    history: [
      { id: "f-1", date: "2025-07-01", type: "Piutang", desc: "Tagihan 156 user bulan Juli", amount: 1500000 },
      { id: "f-2", date: "2025-07-01", type: "Piutang", desc: "Biaya instalasi 5 pelanggan baru", amount: 1000000 },
      { id: "f-3", date: "2025-06-28", type: "Hutang", desc: "Pembelian voucher bulk 50 pcs", amount: 500000 },
      { id: "f-4", date: "2025-06-25", type: "Hutang", desc: "Potongan komisi bulan lalu", amount: 250000 },
      { id: "f-5", date: "2025-06-01", type: "Piutang", desc: "Tagihan 148 user bulan Juni", amount: 1420000 },
    ],
  },
  // User PPPoE terbaru
  recent_users: [
    { id: "u-1", name: "Budi Santoso", internet_number: "ISP-00156", package: "10 Mbps", status: "Aktif", created_at: "2025-07-10" },
    { id: "u-2", name: "Siti Aminah", internet_number: "ISP-00155", package: "20 Mbps", status: "Aktif", created_at: "2025-07-09" },
    { id: "u-3", name: "Ahmad Fauzi", internet_number: "ISP-00154", package: "10 Mbps", status: "Nonaktif", created_at: "2025-07-08" },
    { id: "u-4", name: "Dewi Lestari", internet_number: "ISP-00153", package: "30 Mbps", status: "Aktif", created_at: "2025-07-05" },
    { id: "u-5", name: "Rizki Ramadhan", internet_number: "ISP-00152", package: "20 Mbps", status: "Aktif", created_at: "2025-07-03" },
  ],
  // Voucher terbaru
  recent_vouchers: [
    { id: "v-1", code: "VCH-A1B2C3", package: "10 Mbps", duration: "30 Hari", status: "Aktif", created_at: "2025-07-10" },
    { id: "v-2", code: "VCH-D4E5F6", package: "20 Mbps", duration: "30 Hari", status: "Terpakai", created_at: "2025-07-09" },
    { id: "v-3", code: "VCH-G7H8I9", package: "10 Mbps", duration: "30 Hari", status: "Aktif", created_at: "2025-07-08" },
    { id: "v-4", code: "VCH-J0K1L2", package: "30 Mbps", duration: "30 Hari", status: "Kadaluarsa", created_at: "2025-06-28" },
  ],
};

/* ============================================
   Sub-Komponen: SectionCard
   ============================================ */
const SectionCard = ({ title, icon: Icon, children, className = "" }) => (
  <article
    className={`rounded-2xl p-5 sm:p-6 transition-all duration-300 ${className}`}
    style={{
      background: "rgba(15, 23, 42, 0.5)",
      border: "1px solid rgba(51, 65, 85, 0.3)",
      backdropFilter: "blur(12px)",
    }}
  >
    <div className="flex items-center gap-3 mb-5">
      {Icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{
            background: "rgba(0, 180, 216, 0.1)",
            border: "1px solid rgba(0, 180, 216, 0.2)",
          }}
        >
          <Icon className="text-ocean-400 text-sm" />
        </div>
      )}
      <h3 className="font-display text-base sm:text-lg font-bold text-gray-100">
        {title}
      </h3>
    </div>
    {children}
  </article>
);

/* ============================================
   Sub-Komponen: StatusBadge
   ============================================ */
const StatusBadge = ({ status, size = "sm" }) => {
  const config = {
    "Aktif": "bg-emerald-500/12 text-emerald-400 border-emerald-500/20",
    "Selesai": "bg-emerald-500/12 text-emerald-400 border-emerald-500/20",
    "Terpakai": "bg-cyan-500/12 text-cyan-400 border-cyan-500/20",
    "Nonaktif": "bg-slate-500/12 text-slate-400 border-slate-500/20",
    "Kadaluarsa": "bg-red-500/12 text-red-400 border-red-500/20",
    "Dalam Pengerjaan": "bg-amber-500/12 text-amber-400 border-amber-500/20",
  };

  const dotColor = {
    "Aktif": "bg-emerald-400",
    "Selesai": "bg-emerald-400",
    "Terpakai": "bg-cyan-400",
    "Nonaktif": "bg-slate-400",
    "Kadaluarsa": "bg-red-400",
    "Dalam Pengerjaan": "bg-amber-400",
  };

  const styleClass = config[status] || "bg-slate-500/12 text-slate-400 border-slate-500/20";
  const dot = dotColor[status] || "bg-slate-400";
  const sizeClass = size === "xs" ? "px-2 py-0.5 text-[10px]" : "px-3 py-1 text-xs";

  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-semibold tracking-wide border whitespace-nowrap ${styleClass} ${sizeClass}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
      {status}
    </span>
  );
};

/* ============================================
   Sub-Komponen: StatCard
   ============================================ */
const StatCard = ({ label, value, icon: Icon, color, trend, trendValue }) => {
  const colorMap = {
    cyan: {
      bg: "rgba(0, 180, 216, 0.06)",
      border: "rgba(0, 180, 216, 0.2)",
      text: "text-cyan-400",
      iconBg: "rgba(0, 180, 216, 0.12)",
    },
    emerald: {
      bg: "rgba(16, 185, 129, 0.06)",
      border: "rgba(16, 185, 129, 0.2)",
      text: "text-emerald-400",
      iconBg: "rgba(16, 185, 129, 0.12)",
    },
    amber: {
      bg: "rgba(245, 158, 11, 0.06)",
      border: "rgba(245, 158, 11, 0.2)",
      text: "text-amber-400",
      iconBg: "rgba(245, 158, 11, 0.12)",
    },
    red: {
      bg: "rgba(239, 68, 68, 0.06)",
      border: "rgba(239, 68, 68, 0.2)",
      text: "text-red-400",
      iconBg: "rgba(239, 68, 68, 0.12)",
    },
    violet: {
      bg: "rgba(139, 92, 246, 0.06)",
      border: "rgba(139, 92, 246, 0.2)",
      text: "text-violet-400",
      iconBg: "rgba(139, 92, 246, 0.12)",
    },
  };

  const c = colorMap[color] || colorMap.cyan;

  return (
    <div className="p-4 rounded-xl" style={{ background: c.bg, border: `1px solid ${c.border}` }}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</span>
        {Icon && (
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${c.iconBg}`}>
            <Icon className={`${c.text} text-sm`} />
          </div>
        )}
      </div>
      <p className={`text-2xl font-bold ${c.text} tracking-tight`}>{value}</p>
      {trend && (
        <div className="flex items-center gap-1.5 mt-1.5">
          {trend === "up" ? (
            <FaArrowTrendUp className="text-emerald-400 text-xs" />
          ) : (
            <FaArrowTrendDown className="text-red-400 text-xs" />
          )}
          <span className={`text-[11px] font-semibold ${trend === "up" ? "text-emerald-400" : "text-red-400"}`}>
            {trendValue}
          </span>
          <span className="text-[11px] text-slate-500">vs bulan lalu</span>
        </div>
      )}
    </div>
  );
};

/* ============================================
   Format Currency
   ============================================ */
const formatCurrency = (value) =>
  new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

/* ============================================
   Halaman Utama: MitraDetail
   ============================================ */
export const MitraDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [showSidebar, setShowSidebar] = useState(false);
  const [mitra, setMitra] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulasi fetch API berdasarkan ID
    setTimeout(() => {
      setMitra(DUMMY_MITRA);
      setIsLoading(false);
    }, 800);
  }, [id]);

  if (isLoading || !mitra) {
    return (
      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        <div className="p-6 flex items-center justify-center h-screen">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-10 h-10 rounded-full border-4 border-slate-600 border-t-ocean-400 animate-spin" />
            <p className="text-slate-400">Memuat data mitra...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Hitung persentase perubahan penghasilan
  const earningsChange = mitra.earnings.bulan_lalu > 0
    ? Math.round(((mitra.earnings.bulan_ini - mitra.earnings.bulan_lalu) / mitra.earnings.bulan_lalu) * 100)
    : 0;

  return (
    <>
      <Helmet>
        <title>Detail Mitra {mitra.name} - DANELA NET</title>
      </Helmet>

      <DashboardLayout showSidebar={showSidebar} setShowSidebar={setShowSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setShowSidebar(!showSidebar)}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-slate-800 transition-colors text-slate-400 hover:text-white"
            >
              <FaArrowLeft />
            </button>
            <div className="flex flex-col">
              <h2 className="text-lg md:text-xl font-display font-bold text-gray-100 tracking-tight">
                Detail Mitra
              </h2>
              <p className="text-xs text-slate-500 hidden md:block mt-0.5">
                Informasi lengkap mitra dan statistik
              </p>
            </div>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 space-y-6">

          {/* ── Row 1: Profil + Statistik ── */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Card Profil */}
            <div className="lg:col-span-1">
              <SectionCard title="Profil Mitra" icon={FaUserCircle}>
                <div className="flex flex-col items-center text-center">
                  {!mitra?.image ? (
                    <div
                      className="w-24 h-24 rounded-2xl flex items-center justify-center font-bold text-3xl flex-shrink-0 mb-4"
                      style={{
                        background: "linear-gradient(135deg, rgba(0, 180, 216, 0.15), rgba(0, 119, 182, 0.08))",
                        border: "1px solid rgba(0, 180, 216, 0.15)",
                        color: "#48CAE4",
                      }}
                    >
                      {mitra.name?.charAt(0).toUpperCase()}
                    </div>
                  ) : (
                    <img
                      src={`${BaseUrl}/storage/${mitra.image}`}
                      alt={mitra.name}
                      className="w-24 h-24 rounded-2xl object-cover flex-shrink-0 mb-4 border-2 border-slate-700"
                    />
                  )}

                  <h2 className="text-xl font-bold text-gray-100">{mitra.name}</h2>
                  <div className="mt-2 mb-4">
                    <StatusBadge status={mitra?.status?.name} />
                  </div>

                  <div className="w-full space-y-3 text-left mt-2">
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaBriefcase className="text-ocean-400 w-5" />
                      <span className="text-slate-200 font-medium">{mitra?.role?.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaEnvelope className="text-ocean-400 w-5" />
                      <span className="text-slate-300">{mitra.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaPhone className="text-ocean-400 w-5" />
                      <span className="text-slate-300">{mitra.phone_number}</span>
                    </div>
                    <div className="flex items-start gap-3 text-sm text-slate-400">
                      <FaMapMarkerAlt className="text-ocean-400 w-5 mt-0.5" />
                      <span className="text-slate-300">{mitra.address}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-slate-400">
                      <FaCalendarAlt className="text-ocean-400 w-5" />
                      <span className="text-slate-300">Bergabung: {mitra.join_date}</span>
                    </div>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Grid Statistik */}
            <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <StatCard
                label="Penghasilan Bulan Ini"
                value={formatCurrency(mitra.earnings.bulan_ini)}
                icon={FaMoneyBillWave}
                color="cyan"
                trend={mitra.earnings.trend}
                trendValue={`${earningsChange > 0 ? "+" : ""}${earningsChange}%`}
              />
              <StatCard
                label="User PPPoE"
                value={mitra.stats.total_user_pppoe}
                icon={FaUsers}
                color="emerald"
              />
              <StatCard
                label="Voucher Aktif"
                value={mitra.stats.voucher_aktif}
                icon={FaTicket}
                color="violet"
              />
              <StatCard
                label="Saldo Hutang/Piutang"
                value={formatCurrency(mitra.financials.saldo)}
                icon={FaArrowTrendUp}
                color={mitra.financials.saldo >= 0 ? "emerald" : "red"}
              />
            </div>
          </div>

          {/* ── Row 2: Ringkasan User & Voucher ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div
              className="p-4 rounded-xl flex items-center gap-4"
              style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(16, 185, 129, 0.1)" }}>
                <FaUsers className="text-emerald-400 text-lg" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">User Aktif</p>
                <p className="text-xl font-bold text-emerald-400">{mitra.stats.user_aktif}
                  <span className="text-sm font-normal text-slate-500"> / {mitra.stats.total_user_pppoe}</span>
                </p>
              </div>
            </div>
            <div
              className="p-4 rounded-xl flex items-center gap-4"
              style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(239, 68, 68, 0.1)" }}>
                <FaUsers className="text-red-400 text-lg" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">User Nonaktif</p>
                <p className="text-xl font-bold text-red-400">{mitra.stats.user_nonaktif}</p>
              </div>
            </div>
            <div
              className="p-4 rounded-xl flex items-center gap-4"
              style={{ background: "rgba(15, 23, 42, 0.5)", border: "1px solid rgba(51, 65, 85, 0.3)" }}
            >
              <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(0, 180, 216, 0.1)" }}>
                <FaTicket className="text-cyan-400 text-lg" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold">Voucher Terpakai</p>
                <p className="text-xl font-bold text-cyan-400">{mitra.stats.voucher_terpakai}
                  <span className="text-sm font-normal text-slate-500"> / {mitra.stats.voucher_total}</span>
                </p>
              </div>
            </div>
          </div>

          {/* ── Row 3: Hutang Piutang ── */}
          <SectionCard title="Hutang & Piutang" icon={FaMoneyBillWave}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl" style={{ background: "rgba(16, 185, 129, 0.05)", border: "1px solid rgba(16, 185, 129, 0.2)" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">Total Piutang</span>
                  <FaArrowTrendUp className="text-emerald-400 text-sm" />
                </div>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(mitra.financials.total_piutang)}</p>
              </div>
              <div className="p-4 rounded-xl" style={{ background: "rgba(239, 68, 68, 0.05)", border: "1px solid rgba(239, 68, 68, 0.2)" }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">Total Hutang</span>
                  <FaArrowTrendDown className="text-red-400 text-sm" />
                </div>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(mitra.financials.total_hutang)}</p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-700/50">
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tanggal</th>
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Tipe</th>
                    <th className="pb-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">Keterangan</th>
                    <th className="pb-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">Jumlah</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700/30">
                  {mitra.financials.history.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="py-3 text-slate-400 whitespace-nowrap">{item.date}</td>
                      <td className="py-3">
                        <span className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          item.type === "Piutang"
                            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                            : "bg-red-500/10 text-red-400 border border-red-500/20"
                        }`}>
                          {item.type}
                        </span>
                      </td>
                      <td className="py-3 text-slate-300 font-medium">{item.desc}</td>
                      <td className={`py-3 text-right font-semibold whitespace-nowrap ${
                        item.type === "Piutang" ? "text-emerald-400" : "text-red-400"
                      }`}>
                        {item.type === "Piutang" ? "+" : "-"} {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SectionCard>

          {/* ── Row 4: User PPPoE + Voucher ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* Daftar User PPPoE */}
            <SectionCard title="User PPPoE Terbaru" icon={FaUsers}>
              <div className="space-y-2.5">
                {mitra.recent_users.map((u) => (
                  <div
                    key={u.id}
                    className="p-3 rounded-xl flex items-center justify-between"
                    style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        u.status === "Aktif" ? "bg-emerald-500/10 text-emerald-400" : "bg-slate-500/10 text-slate-400"
                      }`}>
                        <FaWifi className="text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-100 truncate">{u.name}</p>
                        <p className="text-xs text-slate-500 font-mono">{u.internet_number} · {u.package}</p>
                      </div>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <StatusBadge status={u.status} size="xs" />
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>

            {/* Riwayat Voucher */}
            <SectionCard title="Voucher Terbaru" icon={FaTicket}>
              <div className="space-y-2.5">
                {mitra.recent_vouchers.map((v) => (
                  <div
                    key={v.id}
                    className="p-3 rounded-xl flex items-center justify-between"
                    style={{ background: "rgba(15, 23, 42, 0.6)", border: "1px solid rgba(51, 65, 85, 0.2)" }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        v.status === "Aktif" ? "bg-violet-500/10 text-violet-400"
                          : v.status === "Terpakai" ? "bg-cyan-500/10 text-cyan-400"
                          : "bg-red-500/10 text-red-400"
                      }`}>
                        <FaTicket className="text-sm" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-100 font-mono">{v.code}</p>
                        <p className="text-xs text-slate-500">{v.package} · {v.duration}</p>
                      </div>
                    </div>
                    <div className="ml-3 flex-shrink-0">
                      <StatusBadge status={v.status} size="xs" />
                    </div>
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>

          {/* ── Row 5: Penghasilan Tahunan ── */}
          <SectionCard title="Penghasilan Tahun Ini" icon={FaMoneyBillWave}>
            <div className="flex items-center gap-6 flex-wrap">
              <div
                className="flex-1 min-w-[200px] p-5 rounded-xl text-center"
                style={{
                  background: "linear-gradient(135deg, rgba(0, 180, 216, 0.1), rgba(0, 119, 182, 0.05))",
                  border: "1px solid rgba(0, 180, 216, 0.2)",
                }}
              >
                <p className="text-[10px] font-semibold text-ocean-400 uppercase tracking-wider mb-1">Total Penghasilan</p>
                <p className="text-3xl font-bold text-gray-100 tracking-tight">{formatCurrency(mitra.earnings.total_tahun_ini)}</p>
              </div>
              <div className="flex-1 min-w-[200px] grid grid-cols-2 gap-4">
                <div className="text-center p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/10">
                  <p className="text-xl font-bold text-emerald-400">{formatCurrency(mitra.earnings.bulan_ini)}</p>
                  <p className="text-[10px] font-semibold text-emerald-500/70 uppercase mt-1">Bulan Ini</p>
                </div>
                <div className="text-center p-3 rounded-xl bg-slate-800/40 border border-slate-700/30">
                  <p className="text-xl font-bold text-slate-300">{formatCurrency(mitra.earnings.bulan_lalu)}</p>
                  <p className="text-[10px] font-semibold text-slate-500 uppercase mt-1">Bulan Lalu</p>
                </div>
              </div>
            </div>
          </SectionCard>

        </main>
      </DashboardLayout>
    </>
  );
};